import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Mail, MessageCircle } from "lucide-react";
import ImageFrame from "@/components/ImageFrame";

const SECTIONS = [
  {
    n: "01",
    title: "Paid & Organic Campaigns",
    blurb: "Integrated influencer + organic launches that converted curiosity into 5,000+ sign-ups.",
    image: "01 Campaign Section Image.jpg",
    to: "/paid-organic-campaigns",
  },
  {
    n: "02",
    title: "Moment & Culture Marketing, Memes",
    blurb: "Turning trending topics, events & celebrations and memes into finance lessons.",
    image: "02 Moment Marketing.jpg",
    to: "/moment-marketing",
  },
  {
    n: "03",
    title: "Strategic Content IPs",
    blurb: "Recurring, format-driven content that builds loyalty and authority.",
    image: "03 Frameworks.jpg",
    to: "/strategic-content-pillars",
  },
  {
    n: "04",
    title: "Broadcast & Thought Leadership",
    blurb: "Owning the entire production lifecycle: from television studio to podcast feed.",
    image: "04 Television shows and podcasts.jpg",
    to: "/broadcast-thought-leadership",
  },
  {
    n: "05",
    title: "Simplifying Finance Through Storytelling",
    blurb: "Real lives, big lessons: narratives that activate seven areas of the brain.",
    image: "05 Making complex financial concepts accessible and engaging.jpg",
    to: "/simplifying-finance-storytelling",
  },
  {
    n: "06",
    title: "Platform Native Strategies",
    blurb: "Twitter is not Instagram. Translating ideas into the grammar of each platform.",
    image: "06 Platform Native.jpg",
    to: "/platform-native-strategies",
  },
  {
    n: "07",
    title: "Audience Segmentation & Data-Driven Strategy",
    blurb: "Walking away from the beginner race to own the intermediate-to-advanced investor.",
    image: "07 Content Strategy.jpg",
    to: "/audience-segmentation-data-driven",
  },
];

export default function HomePage() {
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative mx-auto max-w-[1440px] px-6 pt-16 lg:px-12 lg:pt-24">
        <div className="fade-up">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4A574]">
            Portfolio
          </p>

          {/* Name + avatar row */}
          <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
            <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-full border border-[#D4A574]/40 bg-[#11151F] shadow-[0_0_40px_rgba(212,165,116,0.18)] sm:h-32 sm:w-32 lg:h-40 lg:w-40">
              <ImageFrame filename="Tinesh photo.jpg" ratio="1/1" />
            </div>
            <div>
              <h1 className="font-serif text-[2.5rem] leading-[1] tracking-tight sm:text-5xl lg:text-[5rem]">
                Tinesh <span className="italic text-[#D4A574]">Bhasin</span>
              </h1>
              <p className="mt-3 font-serif text-lg italic leading-snug text-[#EDE4D3]/75 sm:text-xl lg:text-2xl">
                Social Media &amp; Content Strategist
              </p>
            </div>
          </div>

          <p className="mt-10 max-w-[52ch] text-[15px] leading-relaxed text-[#EDE4D3]/60 hidden">
            Building audiences. Translating markets, mutual funds, and money into stories worth sharing.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="https://www.linkedin.com/in/tineshbhasin/"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-linkedin"
              className="group inline-flex items-center gap-3 rounded-full border border-[#D4A574]/50 bg-[#D4A574]/10 px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[#D4A574] transition-all hover:bg-[#D4A574] hover:text-[#0B0E14]"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:rotate-45" />
            </a>
            <a
              href="mailto:tineshb@gmail.com"
              data-testid="hero-email"
              className="group inline-flex items-center gap-3 rounded-full border border-[#EDE4D3]/20 px-6 py-3 text-[12px] uppercase tracking-[0.22em] transition-all hover:border-[#EDE4D3]"
            >
              <Mail className="h-4 w-4" />
              tineshb@gmail.com
            </a>
            <a
              href="https://wa.me/919833809757"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp"
              className="group inline-flex items-center gap-3 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[#25D366] transition-all hover:bg-[#25D366] hover:text-[#0B0E14]"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:rotate-45" />
            </a>
          </div>
        </div>

        {/* About-strip */}
        <a
          href="https://www.linkedin.com/in/tineshbhasin/"
          target="_blank"
          rel="noreferrer"
          data-testid="about-strip"
          className="group mt-20 flex items-center justify-between border-y border-[#EDE4D3]/12 py-6 transition-colors hover:border-[#D4A574]/40"
        >
          <p className="font-serif text-lg italic md:text-xl lg:text-2xl">
            Building audiences, scaling content operations, growing teams from zero, and leveraging AI to multiply output.
          </p>
          <ArrowUpRight className="h-5 w-5 text-[#EDE4D3]/50 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#D4A574]" />
        </a>
      </section>

      {/* WORK GRID */}
      <section className="mx-auto max-w-[1440px] px-6 pt-24 lg:px-12 lg:pt-32">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">
              Selected Work
            </p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight lg:text-5xl">
              Seven chapters,
              <br />
              <span className="italic text-[#D4A574]">different strategies.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((s, idx) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`home-card-${s.n}`}
              className={`group block fade-up ${
                idx === 0 ? "md:col-span-2 lg:col-span-2" : ""
              }`}
              style={{ animationDelay: `${0.1 + idx * 0.05}s` }}
            >
              <div className="overflow-hidden border border-[#EDE4D3]/10 card-lift">
                <ImageFrame
                  filename={s.image}
                  ratio={idx === 0 ? "16/9" : "4/3"}
                />
              </div>
              <div className="mt-5 flex items-start justify-between gap-6">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4A574]">
                    {s.n}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#EDE4D3]/60">
                    {s.blurb}
                  </p>
                </div>
                <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-[#EDE4D3]/40 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#D4A574]" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
